import React, { useEffect, useState } from "react";
import useApi from "../../../CustomHooks/useApi";
import * as XLSX from "xlsx";
import { FiRefreshCcw } from "react-icons/fi";

const today = () => new Date().toISOString().split("T")[0];

/* ── Skeleton row ── */
const SkeletonRow = () => (
    <tr className="animate-pulse">
        {[50, 110, 130, 100, 110, 100, 100, 100, 110, 100, 100, 110, 100, 100, 100, 100, 130, 100, 110, 110, 130].map((w, i) => (
            <td key={i} className="border border-[#E5E7EB] px-3 py-3.5" style={{ minWidth: w }}>
                <div className="h-3.5 bg-gray-200 rounded-full" style={{ width: "70%" }} />
            </td>
        ))}
    </tr>
);

/* ── Empty state ── */
const EmptyState = ({ onReset }) => (
    <tr>
        <td colSpan={21} className="border-0 py-24 text-center">
            <div className="flex flex-col items-center gap-4 px-6">
                <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                <div>
                    <p className="text-base font-medium text-gray-700">No records found</p>
                    <p className="text-gray-400 text-sm mt-1">Try a different date or click Refresh.</p>
                </div>
                <button onClick={onReset}
                    className="px-5 py-2 rounded-lg bg-[#0A2A4F] text-white font-medium text-sm hover:bg-[#0d3566] transition shadow-sm">
                    Reset & Reload
                </button>
            </div>
        </td>
    </tr>
);

const MerchantLedger = () => {

    /* ── State ── */
    const [date, setDate] = useState(today());
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [downloadError, setDownloadError] = useState("");

    /* ── Pagination ── */
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(50);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [isLastPage, setIsLastPage] = useState(false);
    const [goToPage, setGoToPage] = useState("");

    /* ── API ── */
    const { callApi: fetchSummary } = useApi("/ledger/admin/daily-summary", "GET");

    /* ── Smart pagination ── */
    const getPaginationPages = (current, total) => {
        if (total <= 1) return [];
        if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
        const pages = [];
        const delta = 2;
        let prev = null;
        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current + 1 - delta && i <= current + 1 + delta)) {
                if (prev !== null && i - prev > 1) pages.push("...");
                pages.push(i);
                prev = i;
            }
        }
        return pages;
    };

    const paginationPages = getPaginationPages(page, totalPages);
    const rangeStart = totalElements === 0 ? 0 : page * size + 1;
    const rangeEnd = Math.min(page * size + data.length, totalElements);

    /* ── Fetch ── */
    const fetchData = async (targetPage = 0) => {
        try {
            setLoading(true);
            const query = `?date=${date}&page=${targetPage}&size=${size}`;
            const res = await fetchSummary(null, query);

            if (res && res.content) {
                setData(res.content);
                setPage(res.number ?? targetPage);
                setTotalPages(res.totalPages ?? 0);
                setTotalElements(res.totalElements ?? 0);
                setIsLastPage(res.last === true);
            } else if (Array.isArray(res)) {
                setData(res);
                setPage(0);
                setTotalPages(0);
                setTotalElements(res.length);
                setIsLastPage(true);
            } else {
                setData([]);
            }
        } catch (err) {
            console.error(err);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setPage(0);
        fetchData(0);
    };

    const handleFetchForPage = (targetPage) => {
        setPage(targetPage);
        fetchData(targetPage);
    };

    const handleGoToPage = () => {
        const p = parseInt(goToPage, 10);
        if (isNaN(p) || p < 1 || p > totalPages) {
            setDownloadError(`Enter a page between 1 and ${totalPages}`);
            return;
        }
        setDownloadError("");
        handleFetchForPage(p - 1);
        setGoToPage("");
    };

    useEffect(() => {
        fetchData(0);
        // eslint-disable-next-line
    }, []);

    /* ── Export current page ── */
    const downloadPageExcel = () => {
        if (!data.length) return;
        const ws = XLSX.utils.json_to_sheet(data.map(toExportRow));
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Ledger");
        XLSX.writeFile(wb, `Merchant_Ledger_${date}_page${page + 1}.xlsx`);
    };

    /* ── Export all pages ── */
    const downloadAllExcel = async () => {
        try {
            setExcelLoading(true);
            const exportSize = totalElements > 0 ? totalElements : size;
            const query = `?date=${date}&page=0&size=${exportSize}`;
            const res = await fetchSummary(null, query);
            let rows = [];
            if (res && res.content) rows = res.content;
            else if (Array.isArray(res)) rows = res;
            if (!rows.length) return;
            const ws = XLSX.utils.json_to_sheet(rows.map(toExportRow));
            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, "Ledger");
            XLSX.writeFile(wb, `Merchant_Ledger_${date}_all.xlsx`);
        } catch (err) {
            console.error(err);
        } finally {
            setExcelLoading(false);
        }
    };

    const toExportRow = (r) => ({
        "BP UserId": r.bpUserId ?? "-",
        "Mobile Number": r.mobileNumber ?? "-",
        Date: r.date ?? "-",
        "Main Opening Balance": r.openingBalance ?? 0,
        "Aeps Opening Balance": r.aepsOpeningBalance ?? 0,
        "Commission Opening Balance": r.commissionOpeningBalance ?? 0,
        "Main Credit": r.totalCredit ?? 0,
        "Main Debit": r.totalDebit ?? 0,
        "CashIn Credit": r.cashInCredit ?? 0,
        "AEPS Credit": r.aepsCredit ?? 0,
        "AEPS Debit": r.aepsDebit ?? 0,
        "Commission Credit": r.commissionCredit ?? 0,
        "Commission Debit": r.commissionDebit ?? 0,
        "P2P Credit": r.p2pCredit ?? 0,
        "W2W Credit": r.w2wCredit ?? 0,
        "W2W Debit": r.w2wDebit ?? 0,
        "Total Refund": r.totalRefund ?? 0,
        "Main Balance": r.mainBalance ?? 0,
        "AEPS Balance": r.aepsBalance ?? 0,
        "Commission Balance": r.commissionBalance ?? 0,
    });

    const fmt = (n) =>
        typeof n === "number"
            ? `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
            : "-";

    /* ── Styles ── */
    const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-2 py-2 text-left text-[10px] font-semibold text-[#0A2A4F] uppercase tracking-wide whitespace-nowrap select-none";
    const TD = "border border-[#E5E7EB] px-3 py-2.5 text-[13px] text-gray-700 align-middle";

    /* ════════════════════════ RENDER ════════════════════════ */

    return (
        <div className="h-screen flex flex-col overflow-hidden bg-[#F0F4FA]" style={{ fontFamily: "'Inter','ui-sans-serif',system-ui,sans-serif" }}>

            {/* ══ HEADER ══ */}
            <div className="flex-shrink-0 z-20 shadow-sm">

                {/* Title bar */}
                <div className="bg-white border-b border-[#E5E7EB] px-5 py-3.5 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-9 h-9 rounded-xl bg-[#0A2A4F] flex items-center justify-center text-white text-lg font-medium flex-shrink-0">₹</div>
                        <div className="min-w-0">
                            <div className="flex items-center gap-2.5 flex-wrap">
                                <h1 className="text-[20px] font-normal text-[#0A2A4F] leading-tight">
                                    Merchant Daily Ledger Summary
                                </h1>
                                {totalElements > 0 && (
                                    <span className="px-3 py-1 bg-[#EFF6FF] border border-[#BFDBFE] text-[#0A2A4F] rounded-full text-[13px] font-medium whitespace-nowrap">
                                        {totalElements.toLocaleString("en-IN")} records
                                    </span>
                                )}
                            </div>
                            {totalPages > 0 && (
                                <p className="text-[12px] text-gray-400 font-normal leading-tight mt-0.5">
                                    Page {page + 1} of {totalPages} · {date}
                                </p>
                            )}
                        </div>
                    </div>

                    <div className="flex items-center gap-2 flex-shrink-0">
                        {loading && (
                            <div className="w-4 h-4 border-2 border-[#0A2A4F] border-t-transparent rounded-full animate-spin mr-1" />
                        )}
                        <button onClick={downloadPageExcel} disabled={!data.length}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg border border-[#E5E7EB] bg-white hover:bg-[#EFF6FF] disabled:opacity-40 text-[#0A2A4F] font-medium text-[13px] transition">
                            ↓ Page XLS
                        </button>
                        <button onClick={downloadAllExcel} disabled={excelLoading || !totalElements}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#16A34A] hover:bg-[#15803d] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            ↓ {excelLoading ? "Exporting…" : `Export All${totalElements > 0 ? ` (${totalElements.toLocaleString("en-IN")})` : ""}`}
                        </button>
                        <button onClick={handleRefresh} disabled={loading}
                            className="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[13px] transition">
                            <FiRefreshCcw size={13} className={loading ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Summary strip */}
                <div className="bg-white border-b border-[#E5E7EB] px-4 flex items-stretch divide-x divide-[#E5E7EB] overflow-x-auto">
                    <div className="flex items-center gap-2.5 px-6 py-3 bg-[#EFF6FF] select-none flex-shrink-0">
                        <span className="w-2 h-2 rounded-full flex-shrink-0 bg-[#0A2A4F]" />
                        <span className="text-[12px] font-medium text-gray-400">Total Records</span>
                        <span className="text-[17px] font-normal tabular-nums text-[#0A2A4F]">{totalElements.toLocaleString("en-IN")}</span>
                    </div>
                </div>

                {/* Filter bar */}
                <div className="bg-white border-b border-[#E5E7EB] px-3 py-3 flex gap-2 items-center overflow-x-auto">
                    <span className="text-[13px] text-gray-500 font-medium whitespace-nowrap flex-shrink-0">Date</span>
                    <input
                        type="date"
                        value={date}
                        max={today()}
                        onChange={(e) => setDate(e.target.value)}
                        className="h-[42px] px-3 border border-[#E5E7EB] rounded-lg bg-white text-[14px] text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 focus:border-[#0A2A4F]/40 outline-none flex-shrink-0"
                    />
                    <button onClick={handleRefresh} disabled={loading}
                        className="h-[42px] px-5 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white font-medium text-[14px] transition flex-shrink-0 flex items-center gap-2">
                        <FiRefreshCcw size={14} className={loading ? "animate-spin" : ""} />
                        Apply
                    </button>
                </div>

            </div>

            {/* ══ TABLE ══ */}
            <div className="flex-1 overflow-hidden flex flex-col px-3 pt-3 pb-16">
                <div className="bg-white rounded-xl border border-[#E5E7EB] shadow-sm flex flex-col flex-1 overflow-hidden">

                    {/* Mobile cards */}
                    <div className="block md:hidden flex-1 overflow-y-auto divide-y divide-gray-100">
                        {loading ? (
                            <div className="py-12 flex justify-center">
                                <div className="w-8 h-8 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin" />
                            </div>
                        ) : data.length > 0 ? (
                            data.map((row, i) => (
                                <div key={i} className="px-4 py-4 space-y-2">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <p className="text-[13px] font-semibold text-[#0A2A4F]">{row.bid ?? "-"}</p>
                                            <p className="text-[12px] text-gray-500">{row.mobileNumber ?? "-"}</p>
                                            {row.merchantName && (
                                                <p className="text-[11px] text-gray-400">{row.merchantName}</p>
                                            )}
                                        </div>
                                        <span className="text-[12px] text-gray-400">{row.date ?? "-"}</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px]">
                                        <div className="flex justify-between"><span className="text-gray-400">Main Opening Bal</span><span className="font-medium text-gray-700">{fmt(row.openingBalance)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">AEPS Opening Bal</span><span className="font-medium text-gray-700">{fmt(row.aepsOpeningBalance)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Comm. Opening Bal</span><span className="font-medium text-gray-700">{fmt(row.commissionOpeningBalance)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Main Credit</span><span className="font-medium text-green-600">{fmt(row.totalCredit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Main Debit</span><span className="font-medium text-red-600">{fmt(row.totalDebit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">CashIn Credit</span><span className="text-green-600">{fmt(row.cashInCredit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">AEPS Credit</span><span className="text-green-600">{fmt(row.aepsCredit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">AEPS Debit</span><span className="text-red-600">{fmt(row.aepsDebit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Comm. Credit</span><span className="text-green-600">{fmt(row.commissionCredit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Comm. Debit</span><span className="text-red-600">{fmt(row.commissionDebit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">W2W Credit</span><span className="text-green-600">{fmt(row.w2wCredit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">W2W Debit</span><span className="text-red-600">{fmt(row.w2wDebit)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Refund</span><span className="font-medium text-yellow-600">{fmt(row.totalRefund)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Main Bal</span><span className="font-semibold text-[#0A2A4F]">{fmt(row.mainBalance)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">AEPS Bal</span><span className="font-semibold text-[#0A2A4F]">{fmt(row.aepsBalance)}</span></div>
                                        <div className="flex justify-between"><span className="text-gray-400">Comm. Bal</span><span className="font-semibold text-[#0A2A4F]">{fmt(row.commissionBalance)}</span></div>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <div className="py-16 flex flex-col items-center gap-3 text-center px-4">
                                <div className="text-4xl">📭</div>
                                <p className="text-sm font-medium text-gray-600">No records found</p>
                                <button onClick={handleRefresh} className="px-4 py-2 rounded-lg bg-[#0A2A4F] text-white text-sm">Refresh</button>
                            </div>
                        )}

                        {/* Mobile pagination */}
                        {!loading && totalPages > 1 && (
                            <div className="flex items-center justify-center gap-2 py-4 flex-wrap px-4">
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">‹ Prev</button>
                                <span className="text-[13px] text-gray-500">Page {page + 1} of {totalPages}</span>
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="px-3 py-1.5 rounded-lg border text-[13px] bg-white text-[#0A2A4F] disabled:opacity-30">Next ›</button>
                            </div>
                        )}
                    </div>

                    {/* Desktop table */}
                    <div className="hidden md:block flex-1 overflow-auto">
                        <table className="w-full border-collapse text-[13px]" style={{ minWidth: 1800 }}>
                            <thead className="sticky top-0 z-10">
                                <tr>
                                    <th className={TH} style={{ width: 50 }}>#</th>
                                    <th className={TH}>BP UserId</th>
                                    <th className={TH}>Mobile Number</th>
                                    <th className={TH} style={{ width: 130 }}>Date</th>
                                    <th className={`${TH} text-right`}>Main Opening Balance</th>
                                    <th className={`${TH} text-right`}>Aeps Opening Balance</th>
                                    <th className={`${TH} text-right`}>Comm Opening Bal</th>
                                    <th className={`${TH} text-right`}>Main Credit</th>
                                    <th className={`${TH} text-right`}>Main Debit</th>
                                    <th className={`${TH} text-right`}>CashIn Credit</th>
                                    <th className={`${TH} text-right`}>AEPS Credit</th>
                                    <th className={`${TH} text-right`}>AEPS Debit</th>
                                    <th className={`${TH} text-right`}>Comm Credit</th>
                                    <th className={`${TH} text-right`}>Comm Debit</th>
                                    <th className={`${TH} text-right`}>P2P Credit</th>
                                    <th className={`${TH} text-right`}>W2W Credit</th>
                                    <th className={`${TH} text-right`}>W2W Debit</th>
                                    <th className={`${TH} text-right`}>Total Refund</th>
                                    <th className={`${TH} text-right`} style={{ width: 90 }}>Main Bal</th>
                                    <th className={`${TH} text-right`} style={{ width: 90 }}>AEPS Bal</th>
                                    <th className={`${TH} text-right`} style={{ width: 90 }}>Comm Bal</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    Array.from({ length: 8 }).map((_, i) => <SkeletonRow key={i} />)
                                ) : data.length > 0 ? (
                                    data.map((row, i) => (
                                        <tr key={i} className="hover:bg-[#F8FAFF] transition-colors">
                                            <td className={`${TD} text-gray-400 text-center`}>{rangeStart + i}</td>
                                            <td className={TD}>
                                                <span className="font-medium text-[#0A2A4F]">{row.bpUserId ?? "-"}</span>
                                            </td>
                                            <td className={TD}>
                                                <div>{row.mobileNumber ?? "-"}</div>
                                                {row.merchantName && (
                                                    <div className="text-[11px] text-gray-400 mt-0.5">{row.merchantName}</div>
                                                )}
                                            </td>
                                            <td className={TD}>{row.date ?? "-"}</td>
                                            <td className={`${TD} text-right font-medium text-gray-700`}>{fmt(row.openingBalance)}</td>
                                            <td className={`${TD} text-right font-medium text-gray-700`}>{fmt(row.aepsOpeningBalance)}</td>
                                            <td className={`${TD} text-right font-medium text-gray-700`}>{fmt(row.commissionOpeningBalance)}</td>
                                            <td className={`${TD} text-right font-medium text-green-600`}>{fmt(row.totalCredit)}</td>
                                            <td className={`${TD} text-right font-medium text-red-600`}>{fmt(row.totalDebit)}</td>
                                            <td className={`${TD} text-right text-green-600`}>{fmt(row.cashInCredit)}</td>
                                            <td className={`${TD} text-right text-green-600`}>{fmt(row.aepsCredit)}</td>
                                            <td className={`${TD} text-right text-red-600`}>{fmt(row.aepsDebit)}</td>
                                            <td className={`${TD} text-right text-green-600`}>{fmt(row.commissionCredit)}</td>
                                            <td className={`${TD} text-right text-red-600`}>{fmt(row.commissionDebit)}</td>
                                            <td className={`${TD} text-right text-green-600`}>{fmt(row.p2pCredit)}</td>
                                            <td className={`${TD} text-right text-green-600`}>{fmt(row.w2wCredit)}</td>
                                            <td className={`${TD} text-right text-red-600`}>{fmt(row.w2wDebit)}</td>
                                            <td className={`${TD} text-right font-medium text-yellow-600`}>{fmt(row.totalRefund)}</td>
                                            <td className={`${TD} text-right font-semibold text-[#0A2A4F]`}>{fmt(row.mainBalance)}</td>
                                            <td className={`${TD} text-right font-semibold text-[#0A2A4F]`}>{fmt(row.aepsBalance)}</td>
                                            <td className={`${TD} text-right font-semibold text-[#0A2A4F]`}>{fmt(row.commissionBalance)}</td>
                                        </tr>
                                    ))
                                ) : (
                                    <EmptyState onReset={handleRefresh} />
                                )}
                            </tbody>

                        </table>
                    </div>

                    {/* ── Pagination ── */}
                    {!loading && totalPages > 0 && (
                        <div className="flex-shrink-0 border-t border-[#E5E7EB] bg-white px-4 py-2.5 flex items-center justify-between gap-3 overflow-x-auto flex-nowrap">

                            {/* LEFT: range + Prev / pages / Next */}
                            <div className="flex items-center gap-1.5 flex-nowrap flex-shrink-0">
                                {totalElements > 0 && (
                                    <span className="text-[12px] text-gray-500 font-normal whitespace-nowrap mr-1">
                                        {rangeStart.toLocaleString("en-IN")}–{rangeEnd.toLocaleString("en-IN")} of {totalElements.toLocaleString("en-IN")}
                                    </span>
                                )}
                                <button onClick={() => handleFetchForPage(page - 1)} disabled={page === 0}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    ‹ Prev
                                </button>
                                {paginationPages.map((p, i) =>
                                    p === "..." ? (
                                        <span key={`e${i}`} className="h-8 w-8 flex items-center justify-center text-gray-400 text-sm select-none flex-shrink-0">…</span>
                                    ) : (
                                        <button key={p} onClick={() => handleFetchForPage(p - 1)}
                                            className={`h-8 min-w-[34px] px-2 rounded-lg text-[13px] font-medium transition border flex-shrink-0 ${page === p - 1
                                                ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                                : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F]"
                                            }`}>
                                            {p}
                                        </button>
                                    )
                                )}
                                <button onClick={() => handleFetchForPage(page + 1)} disabled={isLastPage}
                                    className="h-8 px-3.5 rounded-lg border border-[#E5E7EB] bg-white text-[#0A2A4F] text-[13px] font-medium hover:bg-[#EFF6FF] disabled:opacity-30 disabled:cursor-not-allowed transition flex-shrink-0">
                                    Next ›
                                </button>
                            </div>

                            {/* RIGHT: Rows + Go to page */}
                            <div className="flex items-center gap-2 flex-nowrap flex-shrink-0">
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Rows</span>
                                {[50, 100, 500, 1000].map((s) => (
                                    <button key={s} onClick={() => { setSize(s); setPage(0); fetchData(0); }}
                                        className={`h-8 min-w-[44px] px-3 rounded-full text-[13px] font-medium transition border whitespace-nowrap flex-shrink-0 ${size === s
                                            ? "bg-[#0A2A4F] text-white border-[#0A2A4F]"
                                            : "bg-white text-gray-500 border-[#E5E7EB] hover:bg-[#EFF6FF] hover:text-[#0A2A4F] hover:border-[#0A2A4F]/30"
                                        }`}>
                                        {s}
                                    </button>
                                ))}
                                <div className="w-px h-6 bg-[#E5E7EB] flex-shrink-0 mx-1" />
                                <span className="text-[12px] text-gray-400 font-normal whitespace-nowrap">Go to</span>
                                <input type="number" min={1} max={totalPages} placeholder="Page" value={goToPage}
                                    onChange={(e) => { setGoToPage(e.target.value); setDownloadError(""); }}
                                    className="h-8 w-20 px-2.5 border border-[#E5E7EB] rounded-lg bg-white text-[13px] font-normal text-gray-700 focus:ring-2 focus:ring-[#0A2A4F]/20 outline-none flex-shrink-0" />
                                <button onClick={handleGoToPage}
                                    className="h-8 px-4 rounded-lg bg-[#0A2A4F] hover:bg-[#0d3566] text-white font-medium text-[13px] transition flex-shrink-0">
                                    Go
                                </button>
                                {downloadError && (
                                    <span className="text-[12px] text-red-600 font-normal bg-red-50 border border-red-200 rounded-lg px-2 py-1 whitespace-nowrap">
                                        ⚠ {downloadError}
                                    </span>
                                )}
                            </div>

                        </div>
                    )}

                </div>
            </div>

        </div>
    );
};

export default MerchantLedger;
